import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { X, Check } from "lucide-react";
import { comparisonData } from "./mockData";

const KOComparisonSection = () => {
  return (
    <section className="py-24 px-4 bg-background">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">
              Side by Side
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              The Difference Is Night and Day
            </h2>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Without Upfixe */}
          <motion.div
            className="rounded-2xl border-2 border-red-200 overflow-hidden"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-red-50 px-6 py-4 border-b border-red-200">
              <div className="flex items-center gap-2">
                <X className="h-6 w-6 text-red-500" />
                <h3 className="text-lg font-bold text-red-800">Without Upfixe</h3>
              </div>
              <p className="text-sm text-red-600 mt-1">Manual keyword selection</p>
            </div>
            <div className="divide-y divide-red-100">
              {comparisonData.without.map((item, index) => (
                <motion.div
                  key={item.metric}
                  className="px-6 py-4 flex justify-between items-center"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                >
                  <span className="text-sm text-muted-foreground">{item.metric}</span>
                  <span className="text-sm font-bold text-red-700">{item.value}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* With Upfixe */}
          <motion.div
            className="rounded-2xl border-2 border-green-200 overflow-hidden shadow-lg shadow-green-100/50"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-green-50 px-6 py-4 border-b border-green-200">
              <div className="flex items-center gap-2">
                <Check className="h-6 w-6 text-green-600" />
                <h3 className="text-lg font-bold text-green-800">With Upfixe</h3>
              </div>
              <p className="text-sm text-green-600 mt-1">Budget-smart keyword optimisation</p>
            </div>
            <div className="divide-y divide-green-100">
              {comparisonData.with.map((item, index) => (
                <motion.div
                  key={item.metric}
                  className="px-6 py-4 flex justify-between items-center"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.08 }}
                >
                  <span className="text-sm text-muted-foreground">{item.metric}</span>
                  <span className="text-sm font-bold text-green-700">{item.value}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* SEO text */}
        <motion.p
          className="mt-12 text-center text-muted-foreground leading-relaxed max-w-3xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          The numbers speak for themselves. Budget-smart keyword selection transforms the same
          advertising budget from a money pit into a growth engine. Instead of competing against
          enterprise-level advertisers for expensive keywords, Upfixe finds the affordable, high-volume
          alternatives that deliver more clicks, better relevance, and actual conversions.
        </motion.p>
      </div>
    </section>
  );
};

export default KOComparisonSection;
